import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { getApiUrl, apiRequest } from '../config/api';
import { useFacebookSDK } from '../hooks/useFacebookSDK';

// Cada evento de fin trae lo que Meta documenta. Exigir los dos ids en todos
// descarta justo el de coexistencia, que solo manda waba_id.
const EVENTOS_DE_FIN = {
  FINISH: { exigePhone: true },
  FINISH_ONLY_WABA: { exigePhone: true },
  FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING: { exigePhone: false },
};

const PIN_DE_SEIS_DIGITOS = /^\d{6}$/;

function esOrigenDeMeta(origen) {
  try {
    const url = new URL(origen);
    if (url.protocol !== 'https:') return false;
    return url.hostname === 'facebook.com' || url.hostname.endsWith('.facebook.com');
  } catch {
    return false;
  }
}

const mensajeDeError = (err, respaldo) => err.message || err.error || respaldo;

/**
 * Panel "WhatsApp" para un bot. Permite conectar una cuenta de WhatsApp Business
 * usando el Facebook Embedded Signup, en Cloud API (número nuevo) o coexistencia
 * (número que ya usa la app del móvil).
 *
 * Estados:
 *   - CARGANDO      — spinner mientras se obtiene el estado
 *   - NOT_CONNECTED — dos botones según el modo de conexión
 *   - PENDING       — el número quedó pendiente de activación; se puede reintentar
 *   - CONNECTED     — muestra número de teléfono + businessName + desconectar
 */
function WhatsAppSettingsPanel({ botId, botName }) {
  const [status, setStatus] = useState(null);
  const [statusData, setStatusData] = useState(null);
  const [statusError, setStatusError] = useState(null);
  const [connecting, setConnecting] = useState(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [webhookNoSuscrito, setWebhookNoSuscrito] = useState(false);
  const [modoElegido, setModoElegido] = useState(null);
  const [registerPin, setRegisterPin] = useState('');
  const [pinConsulta, setPinConsulta] = useState(null);
  const [consultandoPin, setConsultandoPin] = useState(false);
  const [pinError, setPinError] = useState(null);

  const { ready: fbReady, error: fbError } = useFacebookSDK();
  // El Embedded Signup de Meta no se puede completar en el móvil: no hay URL
  // copiable y FB.login abre un diálogo que en el teléfono no sirve.
  const isMobileUA = useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    return /android|iphone|ipad|ipod/i.test(navigator.userAgent || '');
  }, []);
  const datosDeSesionRef = useRef(null);
  const flujoAbiertoRef = useRef(null);
  const pinDeRegistroRef = useRef(null);
  const pinValido = PIN_DE_SEIS_DIGITOS.test(registerPin);

  const loadStatus = useCallback(async () => {
    setStatusError(null);
    try {
      const res = await apiRequest(getApiUrl(`/whatsapp/status?botId=${botId}`));
      if (!res.ok) {
        setStatusError(`No se pudo obtener el estado (HTTP ${res.status})`);
        setStatus('ERROR');
        return;
      }
      const data = await res.json();
      setStatusData(data);
      setStatus(data.status || 'NOT_CONNECTED');
    } catch (e) {
      setStatusError('Error de red comprobando el estado de WhatsApp');
      setStatus('ERROR');
    }
  }, [botId]);

  useEffect(() => {
    setStatus(null);
    setStatusData(null);
    setActionError(null);
    setWebhookNoSuscrito(false);
    setModoElegido(null);
    setRegisterPin('');
    setPinConsulta(null);
    setConsultandoPin(false);
    setPinError(null);
    datosDeSesionRef.current = null;
    flujoAbiertoRef.current = null;
    pinDeRegistroRef.current = null;
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    function alRecibirMensaje(event) {
      if (flujoAbiertoRef.current !== botId) return;
      if (!esOrigenDeMeta(event.origin)) return;
      let mensaje;
      try {
        mensaje = JSON.parse(event.data);
      } catch {
        return;
      }
      if (mensaje.type !== 'WA_EMBEDDED_SIGNUP') return;
      // Solo la finalización trae los identificadores definitivos. Los eventos
      // intermedios (CANCEL, ERROR, pasos del asistente) dejarían los ids a medias.
      const spec = EVENTOS_DE_FIN[mensaje.event];
      if (!spec) return;
      const { waba_id: wabaId, phone_number_id: phoneNumberId } = mensaje.data || {};
      if (!wabaId) return;
      if (spec.exigePhone && !phoneNumberId) return;
      datosDeSesionRef.current = spec.exigePhone
        ? { wabaId, phoneNumberId, eventoDeMeta: mensaje.event }
        : { wabaId, eventoDeMeta: mensaje.event };
    }

    window.addEventListener('message', alRecibirMensaje);
    return () => window.removeEventListener('message', alRecibirMensaje);
  }, [botId]);

  const aplicarResultadoDeActivacion = useCallback((data) => {
    if (data.webhookSubscribed === false) {
      setWebhookNoSuscrito(true);
    } else if (data.webhookSubscribed === true) {
      setWebhookNoSuscrito(false);
    }
    const phoneNumber = data.displayPhoneNumber || data.phoneNumber;
    if (data.phoneRegistered === false) {
      setStatus('PENDING');
      setStatusData((prev) => ({
        ...(prev || {}),
        ...data,
        phoneNumber: phoneNumber || prev?.phoneNumber,
        activationError: data.activationError || prev?.activationError || null,
      }));
      setActionError(data.activationError || null);
      return;
    }
    setActionError(null);
    return loadStatus();
  }, [loadStatus]);

  const enviarSignup = useCallback(async ({ code, modo, wabaId, phoneNumberId, eventoDeMeta }) => {
    try {
      const payload = { code, botId, onboardingMode: modo };
      if (wabaId) payload.wabaId = wabaId;
      if (phoneNumberId) payload.phoneNumberId = phoneNumberId;
      if (eventoDeMeta) payload.eventoDeMeta = eventoDeMeta;
      if (modo === 'CLOUD_API' && pinDeRegistroRef.current) {
        payload.registerPin = pinDeRegistroRef.current;
      }

      const res = await apiRequest(getApiUrl('/whatsapp/embedded-signup'), {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const data = await res.json();
        await aplicarResultadoDeActivacion(data);
      } else {
        let msg = 'Error procesando el registro';
        try {
          const err = await res.json();
          msg = mensajeDeError(err, msg);
        } catch (_) {}
        setActionError(msg);
      }
    } catch (error) {
      setActionError('Error de conexión al procesar el registro');
    } finally {
      flujoAbiertoRef.current = null;
      setConnecting(null);
    }
  }, [aplicarResultadoDeActivacion, botId]);

  const conectar = useCallback((modo) => {
    if (modo === 'CLOUD_API' && !PIN_DE_SEIS_DIGITOS.test(registerPin)) {
      return;
    }
    if (!fbReady || !window.FB) {
      setActionError('El SDK de Facebook no está listo. Recarga la página.');
      return;
    }
    const configId = process.env.REACT_APP_FB_CONFIG_ID;
    if (!configId) {
      setActionError('REACT_APP_FB_CONFIG_ID no está configurado');
      return;
    }

    flujoAbiertoRef.current = botId;
    datosDeSesionRef.current = null;
    pinDeRegistroRef.current = modo === 'CLOUD_API' ? registerPin : null;
    setConnecting(modo);
    setActionError(null);
    window.FB.login((respuesta) => {
      const code = respuesta?.authResponse?.code;
      if (!code) {
        flujoAbiertoRef.current = null;
        setConnecting(null);
        setActionError('Conexión cancelada');
        return;
      }
      enviarSignup({ code, modo, ...datosDeSesionRef.current });
    }, {
      config_id: configId,
      response_type: 'code',
      override_default_response_type: true,
      extras: {
        setup: {},
        sessionInfoVersion: '3',
        // Sin featureType, Meta muestra la selección de WABA en vez de la pantalla de
        // conectar una cuenta existente, y el flujo de coexistencia no arranca.
        ...(modo === 'COEXISTENCE'
          ? { featureType: 'whatsapp_business_app_onboarding' }
          : {}),
      },
    });
  }, [enviarSignup, fbReady, botId, registerPin]);

  const reintentarActivacion = useCallback(async () => {
    setRetrying(true);
    setActionError(null);
    try {
      const res = await apiRequest(
        getApiUrl(`/whatsapp/reintentar-activacion?botId=${botId}`),
        { method: 'POST' }
      );
      if (res.ok) {
        const data = await res.json();
        await aplicarResultadoDeActivacion(data);
      } else {
        let msg = 'Error reintentando la activación';
        try {
          const err = await res.json();
          msg = mensajeDeError(err, msg);
        } catch (_) {}
        setActionError(msg);
      }
    } catch (error) {
      setActionError('Error de conexión al reintentar la activación');
    } finally {
      setRetrying(false);
    }
  }, [aplicarResultadoDeActivacion, botId]);

  const handleDisconnect = async () => {
    if (!statusData?.botChannelId) return;
    setActionError(null);
    setDisconnecting(true);
    try {
      const response = await apiRequest(
        getApiUrl(`/whatsapp/disconnect?botChannelId=${statusData.botChannelId}`),
        { method: 'DELETE' }
      );
      if (response.ok) {
        setConfirmDisconnect(false);
        await loadStatus();
      } else {
        let msg = 'Error desconectando WhatsApp';
        try {
          const err = await response.json();
          msg = mensajeDeError(err, msg);
        } catch (_) {}
        setActionError(msg);
      }
    } catch (error) {
      setActionError('Error de conexión');
    } finally {
      setDisconnecting(false);
    }
  };

  const consultarPin = async () => {
    setConsultandoPin(true);
    setPinError(null);
    try {
      const res = await apiRequest(getApiUrl(`/whatsapp/pin?botId=${botId}`));
      if (!res.ok) {
        setPinError('No se pudo obtener el PIN');
        return;
      }
      const data = await res.json();
      setPinConsulta({ registerPin: data.registerPin ?? null });
    } catch {
      setPinError('No se pudo obtener el PIN');
    } finally {
      setConsultandoPin(false);
    }
  };

  const alCambiarPin = (event) => {
    setRegisterPin(event.target.value.replace(/\D/g, '').slice(0, 6));
  };

  // Solo el false explícito degrada, y solo si hay una cuenta de verdad. Sin campo
  // (backend antiguo) o con null (NOT_CONNECTED) no se degrada nada; el filtro por
  // status queda de red para un backend que aún serialice false sin número.
  const numeroInactivo = status !== 'NOT_CONNECTED' && statusData?.numberActive === false;
  const isPending = status === 'PENDING' || numeroInactivo;
  const isConnected = status === 'CONNECTED' && !isPending;
  const avisoDeActivacion = actionError || statusData?.activationError;
  const avisoDeWebhook = webhookNoSuscrito ? (
    <div className="ig-settings-warning">
      <strong>No se pudo suscribir el webhook</strong>
      <p>Este bot no recibirá mensajes hasta que se complete la suscripción.</p>
    </div>
  ) : null;

  const botonReintentar = (
    <button
      type="button"
      className="wa-connect-btn"
      onClick={reintentarActivacion}
      disabled={retrying}
    >
      {retrying ? 'Reintentando…' : 'Reintentar activación'}
    </button>
  );

  return (
    <div className="ig-settings-panel">
      {/* Cargando */}
      {status === null && (
        <div className="ig-settings-loading">
          <div className="ig-settings-spinner" />
          <span>Cargando estado de WhatsApp…</span>
        </div>
      )}

      {/* Error de estado */}
      {status === 'ERROR' && (
        <div className="ig-settings-error">
          <p>{statusError || 'No se pudo obtener el estado de WhatsApp.'}</p>
          <button type="button" className="ig-settings-retry-btn" onClick={loadStatus}>
            Reintentar
          </button>
        </div>
      )}

      {/* Pendiente de activación: el 200 no basta, el número no está activo */}
      {isPending && (
        <div className="ig-settings-connect-screen">
          <div className="ig-settings-connect-card whatsapp-connect-card">
            <div className="ig-settings-warning">
              <strong>Pendiente de activación</strong>
              {statusData?.phoneNumber ? (
                <p>Número {statusData.phoneNumber} registrado, pero todavía no puede enviar ni recibir.</p>
              ) : (
                <p>El número quedó registrado, pero todavía no puede enviar ni recibir.</p>
              )}
              {avisoDeActivacion ? <p>{avisoDeActivacion}</p> : null}
            </div>
            {avisoDeWebhook}
            {botonReintentar}
          </div>
        </div>
      )}

      {/* Conectado */}
      {isConnected && (
        <>
          <div className="ig-settings-connected-bar">
            <div className="ig-settings-connected-info">
              <div className="ig-settings-avatar whatsapp-avatar">
                <svg viewBox="0 0 24 24" width="22" height="22" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.832-1.438A9.955 9.955 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div>
                <div className="ig-settings-connected-username">{statusData?.phoneNumber || 'Número desconocido'}</div>
                {statusData?.businessName && (
                  <div className="ig-settings-connected-business">{statusData.businessName}</div>
                )}
                <div className="ig-settings-connected-status">
                  <span className="ig-settings-status-dot whatsapp-dot" /> Cuenta conectada
                </div>
              </div>
            </div>
            <button
              type="button"
              className="ig-settings-disconnect-btn"
              onClick={() => setConfirmDisconnect(true)}
              disabled={disconnecting}
            >
              {disconnecting ? 'Desconectando…' : 'Desconectar'}
            </button>
          </div>

          {actionError && (
            <div className="ig-settings-action-error">{actionError}</div>
          )}
          {avisoDeWebhook}

          <div className="wa-settings-info-section">
            <h3>Información de la cuenta</h3>
            <div className="wa-settings-info-grid">
              {statusData?.phoneNumberId && (
                <div className="wa-settings-info-item">
                  <span className="wa-settings-info-label">Phone Number ID</span>
                  <code className="wa-settings-info-value">{statusData.phoneNumberId}</code>
                </div>
              )}
              {statusData?.wabaId && (
                <div className="wa-settings-info-item">
                  <span className="wa-settings-info-label">WABA ID</span>
                  <code className="wa-settings-info-value">{statusData.wabaId}</code>
                </div>
              )}
              <div className="wa-settings-info-item">
                <span className="wa-settings-info-label">PIN de verificación</span>
                {pinConsulta ? (
                  pinConsulta.registerPin === null ? (
                    <p className="wa-pin-empty">
                      Este número no tiene PIN guardado en la consola porque se conectó antes de este cambio.
                    </p>
                  ) : (
                    <code className="wa-settings-info-value">{pinConsulta.registerPin}</code>
                  )
                ) : (
                  <button
                    type="button"
                    className="wa-pin-reveal-btn"
                    onClick={consultarPin}
                    disabled={consultandoPin}
                  >
                    {consultandoPin ? 'Consultando…' : 'Ver PIN guardado'}
                  </button>
                )}
                {pinError ? <p className="wa-pin-empty">{pinError}</p> : null}
              </div>
            </div>
          </div>
        </>
      )}

      {/* No conectado */}
      {status === 'NOT_CONNECTED' && (
        <div className="ig-settings-connect-screen">
          <div className="ig-settings-connect-card whatsapp-connect-card">
            <div className="ig-settings-logo whatsapp-logo">
              <img src="/wa.svg" alt="WhatsApp" className="wa-original-logo" />
            </div>
            <h3 className="ig-settings-connect-title">
              Conecta tu cuenta de WhatsApp Business
            </h3>
            <p className="ig-settings-connect-desc">
              Vincula tu cuenta de WhatsApp Business para recibir mensajes,
              automatizar respuestas y gestionar conversaciones desde esta plataforma.
            </p>

            {(actionError || fbError) && (
              <div className="ig-settings-action-error">{actionError || fbError}</div>
            )}

            {isMobileUA ? (
              <div className="ig-settings-warning">
                La conexión del canal solo se puede hacer desde un ordenador.
              </div>
            ) : (
              <>
                <div className="wa-connect-actions">
                  {modoElegido === 'CLOUD_API' ? (
                    <div className="wa-pin-block">
                      <label htmlFor="wa-register-pin">PIN de verificación</label>
                      <input
                        id="wa-register-pin"
                        className="wa-pin-input"
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        maxLength={6}
                        value={registerPin}
                        onChange={alCambiarPin}
                        disabled={connecting !== null}
                      />
                      <p className="wa-pin-hint">
                        Es la verificación en dos pasos del número. Meta la pide
                        para borrar el número o cambiar el PIN más adelante, así
                        que conviene guardarlo.
                      </p>
                    </div>
                  ) : null}
                  <button
                    type="button"
                    className="wa-connect-btn"
                    onClick={() => (
                      modoElegido === 'CLOUD_API'
                        ? conectar('CLOUD_API')
                        : setModoElegido('CLOUD_API')
                    )}
                    disabled={
                      connecting !== null
                      || !fbReady
                      || (modoElegido === 'CLOUD_API' && !pinValido)
                    }
                  >
                    {connecting === 'CLOUD_API' ? (
                      <>
                        <span className="ig-connect-btn-spinner" />
                        <span>Conectando…</span>
                      </>
                    ) : !fbReady ? (
                      <>
                        <span className="ig-connect-btn-spinner" />
                        <span>Cargando SDK…</span>
                      </>
                    ) : modoElegido === 'CLOUD_API' ? (
                      <span>Conectar con Facebook</span>
                    ) : (
                      <span>Conectar un número nuevo</span>
                    )}
                  </button>
                  <button
                    type="button"
                    className="wa-connect-btn wa-connect-btn-secondary"
                    onClick={() => {
                      setModoElegido('COEXISTENCE');
                      conectar('COEXISTENCE');
                    }}
                    disabled={connecting !== null || !fbReady}
                  >
                    {connecting === 'COEXISTENCE' ? (
                      <>
                        <span className="ig-connect-btn-spinner" />
                        <span>Conectando…</span>
                      </>
                    ) : !fbReady ? (
                      <>
                        <span className="ig-connect-btn-spinner" />
                        <span>Cargando SDK…</span>
                      </>
                    ) : (
                      <span>Ya uso WhatsApp Business en este número</span>
                    )}
                  </button>
                </div>

                <p className="ig-settings-connect-note">
                  Serás redirigido al proceso de registro de Meta (Facebook).
                  Elige «número nuevo» si el número no está en la app del móvil,
                  o «Ya uso WhatsApp Business» para coexistencia.
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal de confirmación para desconectar */}
      {confirmDisconnect && createPortal(
        <div className="modal-overlay" onClick={() => !disconnecting && setConfirmDisconnect(false)}>
          <div
            className="modal-content confirm-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="wa-disconnect-title"
            onClick={e => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 id="wa-disconnect-title">Desconectar WhatsApp</h3>
              <button
                className="modal-close"
                onClick={() => !disconnecting && setConfirmDisconnect(false)}
                disabled={disconnecting}
              >×</button>
            </div>
            <div className="confirm-modal-body">
              <p>
                ¿Seguro que quieres borrar la configuración de
                {statusData?.phoneNumber ? <> <strong>{statusData.phoneNumber}</strong></> : ' esta cuenta'}
                {botName ? <> en el bot <strong>{botName}</strong></> : null}
                {' '}en la consola?
              </p>
              <p className="confirm-modal-note">
                Esto borra la configuración de la consola. No desvincula el número
                en Meta: en coexistencia eso solo se puede hacer desde la app del
                móvil, en Ajustes &gt; Cuenta &gt; Plataforma de Business.
              </p>
            </div>
            <div className="confirm-modal-actions">
              <button
                type="button"
                className="admin-btn"
                onClick={() => setConfirmDisconnect(false)}
                disabled={disconnecting}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="admin-btn danger"
                onClick={handleDisconnect}
                disabled={disconnecting}
              >
                {disconnecting ? 'Desconectando…' : 'Sí, desconectar'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default WhatsAppSettingsPanel;
